import { Slot } from '@radix-ui/react-slot';
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  cloneElement,
  forwardRef,
  isValidElement,
  memo,
  type ReactElement,
  type ReactNode,
} from 'react';

import { cn, tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const SIDEBAR_NAVIGATION_ITEM_ELEMENT_TAG = 'div';

export const SIDEBAR_NAVIGATION_ITEM_TEST_ID = 'SIDEBAR_NAVIGATION_ITEM';

export const sidebarNavigationItem = tailwindVariants({
  base: 'relative z-0 flex h-8 w-full cursor-pointer items-center gap-2 rounded-4 px-2 text-left text-14 text-grey-10 transition-colors hover:no-underline',
  defaultVariants: {
    state: 'default',
  },
  variants: {
    state: {
      active: 'bg-grey-4 font-medium hover:bg-grey-5',
      default: 'bg-transparent hover:bg-grey-3',
    },
  },
});

export type SidebarNavigationItemsVariants = VariantProps<typeof sidebarNavigationItem>;

export type SidebarNavigationItemProps = Omit<
  ComponentPropsWithoutRef<typeof SIDEBAR_NAVIGATION_ITEM_ELEMENT_TAG>,
  'prefix'
> &
  SidebarNavigationItemsVariants &
  (
    | {
        asChild?: false;
        children: ReactNode;
      }
    | {
        asChild?: true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        children: ReactElement<any>;
      }
  ) &
  Pick<
    ComponentProps<'button'>,
    | 'onClick'
    | 'onMouseDown'
    | 'onMouseEnter'
    | 'onMouseLeave'
    | 'onMouseMove'
    | 'onMouseOut'
    | 'onMouseOver'
    | 'onMouseUp'
  > & {
    buttonClassName?: string;
    prefix?: ReactNode;
    prefixClassName?: string;
    suffix?: ReactNode;
    suffixClassName?: string;
  };

/**
 * @deprecated
 */
export const SidebarNavigationItem = memo(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forwardRef<HTMLButtonElement | any, SidebarNavigationItemProps>(
    (
      {
        asChild,
        buttonClassName,
        children,
        className,
        onClick,
        onMouseDown,
        onMouseEnter,
        onMouseLeave,
        onMouseMove,
        onMouseOut,
        onMouseOver,
        onMouseUp,
        prefix,
        prefixClassName,
        state,
        suffix,
        suffixClassName,
        ...restOfProps
      },
      forwardedRef,
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Component: any = asChild ? Slot : SIDEBAR_NAVIGATION_ITEM_ELEMENT_TAG;

      return (
        <div
          className={sidebarNavigationItem({ class: className, state })}
          data-testid={SIDEBAR_NAVIGATION_ITEM_TEST_ID}
          ref={forwardedRef}
          {...restOfProps}
        >
          {!!prefix && <div className={cn('shrink-0 cursor-auto', prefixClassName)}>{prefix}</div>}

          <Component
            className={cn(
              'flex h-full w-full min-w-0 items-center justify-start gap-2 truncate border-box text-inherit after:absolute after:inset-0 after:-z-1 after:h-full after:w-full after:content-[""] hover:no-underline',
              buttonClassName,
            )}
            onClick={onClick}
            onMouseDown={onMouseDown}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onMouseMove={onMouseMove}
            onMouseOut={onMouseOut}
            onMouseOver={onMouseOver}
            onMouseUp={onMouseUp}
          >
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {asChild && isValidElement<any>(children)
              ? cloneElement(children, {
                  children: children.props.children,
                })
              : children}
          </Component>

          {!!suffix && <div className={cn('shrink-0 cursor-auto', suffixClassName)}>{suffix}</div>}
        </div>
      );
    },
  ),
);

SidebarNavigationItem.displayName = 'SidebarNavigationItem';
